// src/BrowserView.tsx
import {
  useState,
  useEffect,
  useRef,
  useImperativeHandle,
  forwardRef
} from "react";

// src/DevToolsAPI.ts
var DevToolsAPI = class {
  constructor(options = {}) {
    this.ws = null;
    this.commandId = 1;
    this.pendingCommands = /* @__PURE__ */ new Map();
    this.devToolsIframe = null;
    this.messageId = 1;
    this.pendingMessages = /* @__PURE__ */ new Map();
    this.eventListeners = /* @__PURE__ */ new Map();
    this.disconnectListeners = [];
    this.timeout = options.timeout || 5e3;
    this.setupMessageListener();
    window.addEventListener("message", (event) => {
      if (event.data && event.data.type === "devtools-remote-disconnect") {
        this.disconnectListeners.forEach((cb) => cb());
      }
      if (event.data && event.data.type === "devtools-request-url") {
        this.sendCommand("Runtime.evaluate", {
          expression: "window.location.href"
        }).then((result) => {
          if (result && result.result && result.result.value) {
            this.executeInDevTools(`
                (function() {
                  const urlBar = document.querySelector('.custom-url-bar span');
                  if (urlBar) {
                    let cleanUrl = '${result.result.value}';
                    // Remove protocol and www
                    cleanUrl = cleanUrl.replace(/^https?:\\/\\//, '');
                    cleanUrl = cleanUrl.replace(/^www\\./, '');
                    // Remove query parameters
                    cleanUrl = cleanUrl.split('?')[0];
                    urlBar.textContent = cleanUrl;
                  }
                })()
              `);
          }
        }).catch((err) => {
          console.log("Error getting URL", err);
        });
      }
    });
  }
  // ============================================================================
  // LIFECYCLE MANAGEMENT
  // ============================================================================
  async onConnectionReady() {
    try {
      await this.executeInDevTools("localStorage.clear();");
      await this.sendCommand("DOM.enable");
      await this.sendCommand("Page.enable");
      await this.sendCommand("Profiler.enable");
      await this.sendCommand("Inspector.enable");
      await this.sendCommand("Overlay.enable");
    } catch (err) {
      console.error("Failed to enable domains:", err);
    }
  }
  // ============================================================================
  // EVENT SYSTEM
  // ============================================================================
  on(eventName, callback) {
    if (!this.eventListeners.has(eventName)) {
      this.eventListeners.set(eventName, []);
    }
    this.eventListeners.get(eventName).push(callback);
  }
  handleEvent(method, params) {
    const listeners = this.eventListeners.get(method);
    if (listeners) {
      listeners.forEach((cb) => cb(params));
    }
  }
  // ============================================================================
  // CORE CONNECTION AND MESSAGING
  // ============================================================================
  /**
   * Set up message listener for postMessage responses from DevTools iframe
   */
  setupMessageListener() {
    window.addEventListener("message", (event) => {
      var _a;
      if (event.source !== ((_a = this.devToolsIframe) == null ? void 0 : _a.contentWindow))
        return;
      const { type, id, success, result, error } = event.data;
      if (type === "devtools-response" && this.pendingMessages.has(id)) {
        const { resolve, reject } = this.pendingMessages.get(id);
        this.pendingMessages.delete(id);
        if (success) {
          resolve(result);
        } else {
          reject(new Error(error || "DevTools iframe command failed"));
        }
      }
    });
  }
  /**
   * Execute arbitrary JavaScript in the DevTools iframe context
   */
  async executeInDevTools(code) {
    var _a;
    if (!((_a = this.devToolsIframe) == null ? void 0 : _a.contentWindow)) {
      return null;
    }
    const id = this.messageId++;
    return new Promise((resolve, reject) => {
      const timeoutId = setTimeout(() => {
        this.pendingMessages.delete(id);
        reject();
      }, 5e3);
      this.pendingMessages.set(id, {
        resolve: (result) => {
          clearTimeout(timeoutId);
          resolve(result);
        },
        reject: (error) => {
          clearTimeout(timeoutId);
          reject(error);
        }
      });
      this.devToolsIframe.contentWindow.postMessage(
        {
          type: "devtools-command",
          id,
          code
        },
        "*"
      );
    });
  }
  /**
   * Set reference to DevTools iframe for UI manipulation
   */
  setDevToolsIframe(iframe) {
    this.devToolsIframe = iframe;
    iframe.addEventListener("load", async () => {
      try {
        await this.setupScreencastViewportStyleOverride();
        await this.checkForDisconnect();
      } catch (err) {
        console.error("Failed to setup UI manipulation:", err);
      }
    });
  }
  /**
   * Initialize the API by connecting directly to Chrome's debugging WebSocket
   */
  async initialize(wsUrl) {
    return new Promise((resolve, reject) => {
      try {
        this.ws = new WebSocket(wsUrl);
        this.ws.onopen = () => {
          setTimeout(() => {
            this.onConnectionReady();
          }, 200);
          resolve();
        };
        this.ws.onmessage = (event) => {
          try {
            const message = JSON.parse(event.data);
            if (message.id && this.pendingCommands.has(message.id)) {
              const { resolve: cmdResolve, reject: cmdReject } = this.pendingCommands.get(message.id);
              this.pendingCommands.delete(message.id);
              if (message.error) {
                cmdReject(new Error(`CDP Error: ${message.error.message}`));
              } else {
                cmdResolve(message.result);
              }
            }
            if (message.method) {
              this.handleEvent(message.method, message.params);
            }
          } catch (error) {
            console.error("Failed to parse CDP message:", error);
          }
        };
        this.ws.onerror = (error) => {
          console.error("WebSocket error:", error);
          reject(new Error("Failed to connect to Chrome DevTools Protocol"));
        };
        this.ws.onclose = () => {
          this.ws = null;
        };
      } catch (error) {
        reject(new Error(`Failed to initialize DevTools API: ${error}`));
      }
    });
  }
  /**
   * Send a CDP command directly via WebSocket
   */
  async sendCommand(method, params = {}) {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
      return null;
    }
    return new Promise((resolve, reject) => {
      const id = this.commandId++;
      const message = { id, method, params };
      const timeoutId = setTimeout(() => {
        this.pendingCommands.delete(id);
        reject(new Error(`CDP command timeout: ${method}`));
      }, this.timeout);
      this.pendingCommands.set(id, {
        resolve: (result) => {
          clearTimeout(timeoutId);
          resolve(result);
        },
        reject: (error) => {
          clearTimeout(timeoutId);
          reject(error);
        }
      });
      this.ws.send(JSON.stringify(message));
    });
  }
  // ============================================================================
  // PUBLIC API METHODS
  // ============================================================================
  /**
   * Disconnect from the debugging session
   */
  disconnect() {
    if (this.ws) {
      this.ws.close();
      this.ws = null;
    }
    this.pendingCommands.clear();
    this.pendingMessages.clear();
  }
  // ============================================================================
  // UI MANIPULATION METHODS
  // ============================================================================
  /**
   * Hide sidebar elements using shadow DOM manipulation
   */
  async hideSidebar() {
    return this.executeInDevTools(`
      (function() {
        // Find split-widget shadow hosts (these contain the sidebars)
        const splitWidgets = document.querySelectorAll('.split-widget');

        if (splitWidgets.length === 0) {
          return  0
        }

        let hiddenCount = 0;

        splitWidgets.forEach((widget) => {
          if (widget.shadowRoot) {
            // Look for sidebar elements inside the shadow root
            const sidebarSelectors = [
              '.split-widget-sidebar',
              '.shadow-split-widget-sidebar',
              '.split-widget-resizer',
              '.shadow-split-widget-resizer',
              '[class*="sidebar"]'
            ];

            sidebarSelectors.forEach(selector => {
              const sidebarElements = widget.shadowRoot.querySelectorAll(selector);
              sidebarElements.forEach(sidebar => {
                const computedStyle = getComputedStyle(sidebar);

                // Only hide if it's actually visible
                if (computedStyle.display !== 'none' && computedStyle.width !== '0px') {
                  sidebar.style.display = 'none';
                  sidebar.style.width = '0';
                  sidebar.style.minWidth = '0';
                  hiddenCount++;
                }
              });
            });

            // Also hide any element with width that looks like a sidebar
            const allShadowElements = widget.shadowRoot.querySelectorAll('*');
            allShadowElements.forEach(el => {
              const computedStyle = getComputedStyle(el);

              // Look for elements that might be sidebars
              if ((el.className.includes('sidebar') || el.className.includes('resizer')) &&
                  computedStyle.display !== 'none') {
                el.style.display = 'none';
                hiddenCount++;
              }
            });
          }
        });

        return hiddenCount;
      })()
    `);
  }
  /**
   * Show sidebar elements
   */
  async showSidebar() {
    return this.executeInDevTools(`
      (function() {
        // Find split-widget shadow hosts
        const splitWidgets = document.querySelectorAll('.split-widget');
        let shownCount = 0;

        splitWidgets.forEach((widget) => {
          if (widget.shadowRoot) {
            // Look for sidebar elements inside the shadow root
            const sidebarSelectors = [
              '.split-widget-sidebar',
              '.shadow-split-widget-sidebar',
              '.split-widget-resizer',
              '.shadow-split-widget-resizer',
              '[class*="sidebar"]'
            ];

            sidebarSelectors.forEach(selector => {
              const sidebarElements = widget.shadowRoot.querySelectorAll(selector);
              sidebarElements.forEach(sidebar => {
                sidebar.style.display = '';
                sidebar.style.width = '';
                sidebar.style.minWidth = '';
                shownCount++;
              });
            });

            // Also restore any element that was hidden by width detection
            const allShadowElements = widget.shadowRoot.querySelectorAll('*');
            allShadowElements.forEach(el => {
              // Restore elements that might be sidebars
              if (el.className.includes('sidebar') || el.className.includes('resizer')) {
                el.style.display = '';
                shownCount++;
              }
            });
          }
        });

        return { shownCount };
      })()
    `);
  }
  /**
   * Hide navigation bar elements
   */
  async hideNavigationBar() {
    return this.executeInDevTools(`
      (function() {
        let hiddenCount = 0;

        // Common navigation selectors
        const navSelectors = [
          '.screencast-navigation',
          '.toolbar-button',
          '.toolbar-controls',
          '.inspector-view-toolbar',
          '.screencast-navigation-toolbar'
        ];

        // Hide in main document
        navSelectors.forEach(selector => {
          document.querySelectorAll(selector).forEach(el => {
            el.style.display = 'none';
            hiddenCount++;
          });
        });

        // Hide in shadow DOM
        document.querySelectorAll('*').forEach(host => {
          if (host.shadowRoot) {
            navSelectors.forEach(selector => {
              host.shadowRoot.querySelectorAll(selector).forEach(el => {
                el.style.display = 'none';
                hiddenCount++;
              });
            });
          }
        });

        return hiddenCount;
      })()
    `);
  }
  /**
   * Show navigation bar elements
   */
  async showNavigationBar() {
    return this.executeInDevTools(`
      (function() {
        let shownCount = 0;

        // Common navigation selectors
        const navSelectors = [
          '.screencast-navigation',
          '.toolbar-button',
          '.toolbar-controls',
          '.inspector-view-toolbar',
          '.screencast-navigation-toolbar'
        ];

        // Show in main document
        navSelectors.forEach(selector => {
          document.querySelectorAll(selector).forEach(el => {
            el.style.display = '';
            shownCount++;
          });
        });

        // Show in shadow DOM
        document.querySelectorAll('*').forEach(host => {
          if (host.shadowRoot) {
            navSelectors.forEach(selector => {
              host.shadowRoot.querySelectorAll(selector).forEach(el => {
                el.style.display = '';
                shownCount++;
              });
            });
          }
        });

        return { shownCount };
      })()
    `);
  }
  /**
   * Automatically override .screencast-viewport CSS in split-widget shadow roots
   */
  async setupScreencastViewportStyleOverride() {
    return this.executeInDevTools(`
      (function() {
        function overrideScreencastViewport() {
          const splitWidgets = document.querySelectorAll('.split-widget');
          splitWidgets.forEach(widget => {
            const screencast = widget.querySelector('.screencast');
            if (screencast) {
              screencast.style.setProperty('background', 'white', 'important');
            }

            const viewports = widget.querySelectorAll('.screencast-viewport');
            viewports.forEach(vp => {
              vp.style.setProperty('border-color', 'white', 'important');
              vp.style.setProperty('background', 'white', 'important');
            });

            const canvasContainer = widget.querySelector('.screencast-canvas-container');
            if (canvasContainer) {
              canvasContainer.style.setProperty('background', 'white', 'important');
              canvasContainer.style.setProperty('border-radius', '8px', 'important');
              canvasContainer.style.setProperty('border', '2px solid transparent', 'important');
              canvasContainer.style.setProperty('box-shadow', '0 4px 12px -2px rgba(0, 0, 0, 0.15)', 'important');

              // Add URL bar if it doesn't exist
              let urlBar = canvasContainer.querySelector('.custom-url-bar');
              if (!urlBar) {
                urlBar = document.createElement('div');
                urlBar.className = 'custom-url-bar';
                urlBar.style.cssText = \`
                  position: absolute;
                  width: 100%;
                  top: -1px;
                  left: 0;
                  right: 0;
                  height: 32px;
                  background: #ffffff;
                  border-radius: 8px 8px 0 0;
                  display: flex;
                  align-items: center;
                  justify-content: center;
                  padding: 0 16px;
                  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
                  font-size: 12px;
                  color: #6b7280;
                  z-index: 1000;
                \`;

                const urlText = document.createElement('span');
                urlText.style.cssText = \`
                  background: #f3f4f6;
                  border-radius: 4px;
                  padding: 2px 12px;
                  height: 20px;
                  line-height: 20px;
                  overflow: hidden;
                  text-overflow: ellipsis;
                  white-space: nowrap;
                  text-align: center;
                  width: 500px;
                  max-width: 500px;
                  display: inline-block;
                  font-weight: 500;
                  color: #374151;
                  display: flex;
                  align-items: center;
                  justify-content: center;
                \`;
                urlText.textContent = 'Loading...';
                urlBar.appendChild(urlText);

                canvasContainer.style.setProperty('position', 'relative', 'important');
                canvasContainer.appendChild(urlBar);
              }

              // Update URL periodically
              try {
                // Request URL from main context
                window.top.postMessage({
                  type: 'devtools-request-url',
                  urlBarElement: true
                }, '*');

              } catch (e) {
                console.log('Could not set up URL bar communication:', e);
              }

              const canvas = widget.querySelector('.screencast-canvas-container canvas');
              if (canvas) {
                canvas.style.setProperty('border-radius', '8px', 'important');
              }
            }
          });
        }
        // Initial run
        overrideScreencastViewport();

        // Throttle function to limit calls
        let throttleTimeout = null;
        const throttledOverride = () => {
          if (throttleTimeout) return;
          throttleTimeout = setTimeout(() => {
            overrideScreencastViewport();
            throttleTimeout = null;
          }, 250);
        };

        // Observe for new split-widgets and attribute changes
        const observer = new MutationObserver(() => {
          throttledOverride();
        });
        observer.observe(document.body, {
          childList: true,
          subtree: true,
          attributes: true,
          attributeFilter: ['style', 'class']
        });
        window.__screencastViewportObserver = observer;
        return true;
      })()
    `);
  }
  onDisconnect(callback) {
    this.disconnectListeners.push(callback);
  }
  /**
   * Observe for remote debugging disconnect dialog in DevTools UI
   */
  async checkForDisconnect() {
    return this.executeInDevTools(`
      (function() {
        function notifyDisconnect() {
          window.top.postMessage({ type: 'devtools-remote-disconnect' }, '*');
        }

        function checkForDialogInOverlay() {
          const host = document.querySelector('div[data-devtools-glass-pane]');
          if (host && host.shadowRoot) {
            // The DEBNUGGING typo is intentional. This is how they have it spelled.
            const dialog = host.shadowRoot.querySelector('div.widget[jslog*="remote-debnugging-terminated"]');
            if (dialog) notifyDisconnect();
          }
        }

        // Observe for overlay being added
        const overlayObserver = new MutationObserver(() => {
          checkForDialogInOverlay();
        });
        overlayObserver.observe(document.body, { childList: true, subtree: true });
        window.__devtoolsOverlayObserver = overlayObserver;

        // Initial check in case it's already present
        checkForDialogInOverlay();
      })()
    `);
  }
};
async function createDevToolsAPI(wsUrl) {
  const api = new DevToolsAPI();
  try {
    await api.initialize(wsUrl);
  } catch (error) {
    console.error("Failed to initialize DevTools API:", error);
  }
  return api;
}

// src/BrowserView.tsx
import { jsx, jsxs } from "react/jsx-runtime";
var BrowserView = forwardRef(
  ({
    wsUrl,
    devToolsBaseUrl = "https://browser-inspector.pluto.xyz",
    onConnectionChange,
    className = "",
    style = {},
    onPageTitleChange
  }, ref) => {
    const [devToolsAPI, setDevToolsAPI] = useState(null);
    const [isConnected, setIsConnected] = useState(false);
    const [pageTitle, setPageTitle] = useState("");
    const devToolsIframeRef = useRef(null);
    useEffect(() => {
      if (isConnected) {
        setInterval(async () => {
          const response = await (devToolsAPI == null ? void 0 : devToolsAPI.sendCommand("Runtime.evaluate", {
            expression: "document.title"
          }));
          if (response) {
            if (response.result.value !== pageTitle) {
              setPageTitle(response.result.value || "");
              onPageTitleChange(response.result.value || "");
            }
          }
        }, 500);
      }
    }, [isConnected]);
    const connect = async (url) => {
      try {
        setIsConnected(false);
        if (devToolsAPI) {
          devToolsAPI.disconnect();
        }
        const api = await createDevToolsAPI(url);
        setDevToolsAPI(api);
        if (devToolsIframeRef.current) {
          api.setDevToolsIframe(devToolsIframeRef.current);
          api.onDisconnect(() => {
            setIsConnected(false);
            onConnectionChange == null ? void 0 : onConnectionChange(false);
          });
        }
      } catch (error) {
        console.error(`Failed to connect to DevTools: ${error}`);
        setIsConnected(false);
        onConnectionChange == null ? void 0 : onConnectionChange(false);
      }
    };
    useImperativeHandle(ref, () => ({
      showSidebar: async () => {
        if (!devToolsAPI) {
          throw new Error("DevTools not connected");
        }
        try {
          await devToolsAPI.showSidebar();
        } catch (error) {
        }
      },
      hideSidebar: async () => {
        if (!devToolsAPI) {
          throw new Error("DevTools not connected");
        }
        try {
          await devToolsAPI.hideSidebar();
        } catch (error) {
        }
      },
      hideNavigationBar: async () => {
        if (!devToolsAPI) {
          throw new Error("DevTools not connected");
        }
        try {
          await devToolsAPI.hideNavigationBar();
        } catch (error) {
        }
      },
      sendCommand: async (command, params = {}) => {
        if (!devToolsAPI) {
          throw new Error("DevTools not connected");
        }
        return await devToolsAPI.sendCommand(command, params);
      },
      showNavigationBar: async () => {
        if (!devToolsAPI) {
          throw new Error("DevTools not connected");
        }
        try {
          await devToolsAPI.showNavigationBar();
        } catch (error) {
        }
      },
      connect,
      disconnect: () => {
        if (devToolsAPI) {
          devToolsAPI.disconnect();
          setDevToolsAPI(null);
        }
        setIsConnected(false);
        onConnectionChange == null ? void 0 : onConnectionChange(false);
      },
      isConnected: () => isConnected
    }));
    useEffect(() => {
      if (wsUrl && !isConnected) {
        connect(wsUrl);
      }
    }, [wsUrl, isConnected]);
    useEffect(() => {
      return () => {
        if (devToolsAPI) {
          devToolsAPI.disconnect();
        }
      };
    }, [devToolsAPI]);
    const devToolsUrl = wsUrl ? (() => {
      const isWss = wsUrl.trim().startsWith("wss://");
      const cleanUrl = wsUrl.replace("wss://", "").replace("ws://", "").trim();
      return `${devToolsBaseUrl}/inspector.html?${isWss ? "wss" : "ws"}=${encodeURIComponent(cleanUrl)}`;
    })() : "";
    if (!devToolsUrl) {
      return /* @__PURE__ */ jsx(
        "div",
        {
          className: `flex items-center justify-center h-full ${className}`,
          style,
          children: /* @__PURE__ */ jsxs("div", { className: "text-center max-w-sm mx-auto px-6", children: [
            /* @__PURE__ */ jsx("span", { className: "w-full h-full flex items-center justify-center", children: /* @__PURE__ */ jsx("div", { className: "w-10 h-10 mx-auto mb-3 bg-gray-100 rounded-full flex items-center justify-center", children: /* @__PURE__ */ jsxs(
              "svg",
              {
                className: "w-5 h-5 text-gray-400",
                fill: "none",
                viewBox: "0 0 24 24",
                stroke: "currentColor",
                children: [
                  /* @__PURE__ */ jsx(
                    "path",
                    {
                      strokeLinecap: "round",
                      strokeLinejoin: "round",
                      strokeWidth: 1.5,
                      d: "M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"
                    }
                  ),
                  /* @__PURE__ */ jsx(
                    "path",
                    {
                      strokeLinecap: "round",
                      strokeLinejoin: "round",
                      strokeWidth: 1.5,
                      d: "M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                    }
                  )
                ]
              }
            ) }) }),
            /* @__PURE__ */ jsx("h3", { className: "text-base font-medium text-gray-900 mb-2", children: "Ready to Debug" }),
            /* @__PURE__ */ jsx("p", { className: "text-sm text-gray-500 mb-3", children: "Connect to a Chrome target to start debugging" })
          ] })
        }
      );
    }
    return /* @__PURE__ */ jsxs("div", { className: `bg-white h-full ${className}`, style, children: [
      /* @__PURE__ */ jsx(
        "iframe",
        {
          onLoad: async () => {
            let totalNavigationCount = 0;
            const hiddenCountNavigation = new Promise(async (resolve) => {
              while (true) {
                const hiddenCountNavigation2 = await (devToolsAPI == null ? void 0 : devToolsAPI.hideNavigationBar());
                if (hiddenCountNavigation2 && hiddenCountNavigation2 > 0) {
                  totalNavigationCount += hiddenCountNavigation2;
                  if (totalNavigationCount >= 19) {
                    resolve();
                    break;
                  }
                }
                await new Promise((r) => setTimeout(r, 100));
              }
            });
            let totalSidebarCount = 0;
            const hiddenCountSidebar = new Promise(async (resolve) => {
              while (true) {
                const hiddenCountSidebar2 = await (devToolsAPI == null ? void 0 : devToolsAPI.hideSidebar());
                if (hiddenCountSidebar2 && hiddenCountSidebar2 > 0) {
                  totalSidebarCount += hiddenCountSidebar2;
                  if (totalSidebarCount >= 4) {
                    resolve();
                    break;
                  }
                }
                await new Promise((r) => setTimeout(r, 100));
              }
            });
            await Promise.all([hiddenCountNavigation, hiddenCountSidebar]);
            setTimeout(() => {
              onConnectionChange == null ? void 0 : onConnectionChange(true);
              setIsConnected(true);
            }, 300);
          },
          ref: devToolsIframeRef,
          src: devToolsUrl,
          className: `w-full h-full border-0 ${isConnected ? "block" : "hidden"}`,
          title: "Chrome DevTools"
        }
      ),
      !isConnected && /* @__PURE__ */ jsx("div", { className: `absolute inset-0 flex items-center justify-center`, children: /* @__PURE__ */ jsx("h3", { className: "text-base font-medium text-gray-900 mb-2", children: "Initializing..." }) })
    ] });
  }
);
BrowserView.displayName = "BrowserView";
var BrowserView_default = BrowserView;
export {
  BrowserView_default as BrowserView
};
